import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plane, Plus, Check, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = { start_date: "", end_date: "", reason: "" };
const STATUS = {
  pending: { label: "Pending", cls: "bg-orange-500" },
  approved: { label: "Approved", cls: "bg-primary" },
  declined: { label: "Declined", cls: "bg-destructive" },
};

export default function TimeOff() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState("");

  useEffect(() => { load(); }, []);
  async function load() {
    try {
      const my = await api.get("/staff/time-off/my"); setMine(my.data);
      if (isAdmin) { const a = await api.get("/staff/time-off"); setAll(a.data); }
    } catch { /* */ }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.start_date || !form.end_date) return toast.error("Pick a start and end date.");
    if (form.end_date < form.start_date) return toast.error("End date can't be before start date.");
    setLoading(true);
    try {
      await api.post("/staff/time-off", form);
      toast.success("Time-off request submitted for approval.");
      setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  async function approve(id) {
    try { await api.patch(`/staff/time-off/${id}/decision`, { approved: true }); toast.success("Approved."); load(); }
    catch { toast.error("Failed."); }
  }
  async function confirmDecline() {
    try {
      await api.patch(`/staff/time-off/${declineTarget.id}/decision`, { approved: false, decline_reason: declineReason });
      toast.success("Declined. The worker has been notified.");
      setDeclineTarget(null); setDeclineReason(""); load();
    } catch { toast.error("Failed."); }
  }

  const pending = all.filter((r) => r.status === "pending");

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><Plane className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Time Off</h1>
          <p className="text-sm text-muted-foreground">{isAdmin ? "Review and decide on worker time-off requests." : "Request time off and track your requests."}</p>
        </div>
      </div>

      {!isAdmin && (
        <form onSubmit={submit} className="mt-6 grid gap-3 rounded-xl border border-border/60 bg-card p-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5"><Label className="text-xs">Start date</Label><Input type="date" data-testid="timeoff-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required /></div>
          <div className="space-y-1.5"><Label className="text-xs">End date</Label><Input type="date" data-testid="timeoff-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required /></div>
          <div className="space-y-1.5 lg:col-span-2"><Label className="text-xs">Reason (optional)</Label><Input data-testid="timeoff-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
          <div className="lg:col-span-4"><Button type="submit" disabled={loading} className="rounded-full" data-testid="timeoff-submit"><Plus className="mr-1 h-4 w-4" /> Request Time Off</Button></div>
        </form>
      )}

      {isAdmin && (
        <div className="mt-8">
          <h2 className="font-heading text-lg font-bold">Pending requests {pending.length > 0 && <Badge className="ml-1 bg-orange-500">{pending.length}</Badge>}</h2>
          <div className="mt-3 space-y-3">
            {pending.length === 0 ? <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">No pending requests.</div>
              : pending.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-card p-5" data-testid={`timeoff-pending-${r.id}`}>
                  <div>
                    <p className="font-heading font-bold">{r.worker_name}</p>
                    <p className="text-sm text-muted-foreground">{r.start_date} → {r.end_date}{r.reason ? ` • ${r.reason}` : ""}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="rounded-full" onClick={() => approve(r.id)} data-testid={`timeoff-approve-${r.id}`}><Check className="mr-1 h-3 w-3" /> Approve</Button>
                    <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => { setDeclineReason(""); setDeclineTarget(r); }} data-testid={`timeoff-decline-${r.id}`}><X className="mr-1 h-3 w-3" /> Decline</Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="font-heading text-lg font-bold">{isAdmin ? "All requests" : "My requests"}</h2>
        <div className="mt-3 space-y-3">
          {(isAdmin ? all : mine).length === 0 ? <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="timeoff-empty">No requests yet.</div>
            : (isAdmin ? all : mine).map((r) => {
              const st = STATUS[r.status] || STATUS.pending;
              return (
                <div key={r.id} className="rounded-xl border border-border/60 bg-card p-5" data-testid={`timeoff-item-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-heading font-bold">{isAdmin ? r.worker_name : `${r.start_date} → ${r.end_date}`}</p>
                      <p className="text-sm text-muted-foreground">{isAdmin ? `${r.start_date} → ${r.end_date}` : (r.reason || "No reason given")}</p>
                    </div>
                    <Badge className={st.cls} data-testid={`timeoff-status-${r.id}`}>{st.label}</Badge>
                  </div>
                  {r.status === "declined" && r.decline_reason && (
                    <p className="mt-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive" data-testid={`timeoff-declinereason-${r.id}`}>
                      Manager note: {r.decline_reason}
                    </p>
                  )}
                  {!isAdmin && r.status === "declined" && (
                    <p className="mt-2 text-xs text-muted-foreground">Declined — you can submit a new request above for a different or shorter date range.</p>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      <AlertDialog open={!!declineTarget} onOpenChange={(o) => { if (!o) { setDeclineTarget(null); setDeclineReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline {declineTarget?.worker_name}'s request?</AlertDialogTitle>
            <AlertDialogDescription>Add a reason the worker will see so they can submit a new or shorter request.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea data-testid="timeoff-decline-reason" placeholder="e.g. Short-staffed that week — please try a shorter range." value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDecline(); }} data-testid="timeoff-decline-confirm" className="bg-destructive hover:bg-destructive/90">Decline Request</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
